import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import clsx from 'clsx';

import APResponsiveDropdown from 'components/Dropdown/APResponsiveDropdown';
import APDropdown from 'components/Dropdown/APDropdown';
import CalendarPicker from 'components/CalendarPicker';
import SubmitInputField from 'components/FormFields/SubmitInputField';
import { useTimeframe } from 'hooks/useTimeframe';

dayjs.extend(utc);

const ChecklistDueDate = ({
  date,
  timeframe,
  module,
  placeholder,
  isEditing,
  pushDate,
  reminders,
  taskId,
  scope,
  isMyFocus,
  createTaskReminder,
  updateTaskReminder,
  deleteTaskReminder,
  ...restProps
}) => {
  const ref = useRef(null);
  const { toString, fromString, ERROR_MSG } = useTimeframe();
  const [showCalendar, setShowCalendar] = useState(false);
  const [hasTimeframeError, setTimeframeError] = useState(false);
  // This logic needs to be improved but best simulates 1.0 notification center at the moment.
  const isTimeframe = module === 'weddings' && !date;
  const fieldValue = date ? dayjs(date).format('MMM Do') : timeframe ? toString(timeframe) : null;
  const calendarDate = date ? dayjs(date).utc().local().format() : null;

  const openCalendar = (e) => {
    if (isEditing) e.stopPropagation();
    setShowCalendar(true);
  };

  const closeCalendar = (e) => {
    if (e?.target === ref.current) return;
    setShowCalendar(false);
  };

  const handleCalendarClick = (selectedDate) => {
    if (!selectedDate) return;
    const convertedDate = dayjs(selectedDate).utc().local().format();
    pushDate(convertedDate);
  };

  const handleDateClear = () => {
    pushDate(null);
  };

  const attemptSave = (e) => {
    const { value } = e.target;
    const newTimeframe = fromString(value);

    if (newTimeframe) {
      if (ref.current) ref.current.value = toString(newTimeframe);
      pushDate(newTimeframe, 'timeframe');
      setTimeframeError(false);
    }
    else if (ref.current) {
      ref.current.value = fieldValue;
    }
  };

  const validateTimeframe = (e) => {
    if (!fromString(e.target.value)) {
      setTimeframeError(true);
    }
    else {
      setTimeframeError(false);
    }
  };

  useEffect(() => {
    if (!isTimeframe && !isEditing) ref.current.value = dayjs(date).format('MMM Do');
  }, [date]);

  useEffect(() => {
    if (!isEditing) setShowCalendar(false);
  }, [isEditing]);

  return (
    <>
      <div
        ref={!isTimeframe ? ref : null}
        className={clsx('nc-checklist__due-date-label', isEditing && isTimeframe && 'hidden')}
        onClick={openCalendar}
      >
        {fieldValue}
        {!fieldValue && isEditing && (
          <span className="placeholder">Due Date</span>
        )}
      </div>
      {isTimeframe && (
        <SubmitInputField
          ref={ref}
          defaultValue={fieldValue}
          placeholder={placeholder}
          onFocus={openCalendar}
          onBlur={attemptSave}
          onChange={validateTimeframe}
          className={clsx(!isEditing && 'hidden', hasTimeframeError && 'error')}
          {...restProps}
        />
      )}
      {ref.current && isEditing && (
        <APResponsiveDropdown
          menuClass="nc-checklist__calendar-picker-wrapper"
          opened={showCalendar}
          dismiss={closeCalendar}
          anchorElement={ref.current}
          backdrop={false}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              // Prevent propagation on document event listener.
              e.nativeEvent.stopImmediatePropagation();
            }}
          >
            <CalendarPicker
              selected={calendarDate}
              onDateChange={handleCalendarClick}
              onDateClear={handleDateClear}
              nPastYears={0}
              showNextYears
              showReminder
              entityId={taskId}
              reminders={!isTimeframe ? reminders : []}
              createReminder={(params) => createTaskReminder({ ...params, module, scope, isMyFocus })}
              updateReminder={(params) => updateTaskReminder({ ...params, module, scope, isMyFocus })}
              deleteReminder={(params) => deleteTaskReminder({ ...params, module, scope, isMyFocus })}
              closeCalendar={closeCalendar}
            />
          </div>
        </APResponsiveDropdown>
      )}
      {hasTimeframeError && (
        <APDropdown
          isOpen={hasTimeframeError}
          menuClass="ap-dropdown__menu--date-error nc-checklist__date-error"
          anchorElement={ref.current}
        >
          <div
            className="comment-delete"
            onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
          >
            <div className="comment-delete__message">
              {ERROR_MSG}
            </div>
          </div>
        </APDropdown>
      )}
    </>
  );
};

export default ChecklistDueDate;
